/**
 * Date range for the "Active Claims By Responsibility" widget (FE-151).
 *
 * Dates are date-only `YYYY-MM-DD` strings throughout, so they compare lexicographically and never
 * shift with the browser's timezone. All arithmetic is done in UTC on those strings.
 *
 * The UI end date is INCLUSIVE; `GET /provider/report/responsibility` takes an EXCLUSIVE end and
 * filters `Claim.CreateDate` in `[start, end)`. It rejects `end <= start` and ranges longer than one
 * calendar year (400), so `validateResponsibilityRange` mirrors those rules before a request is sent.
 */

export type ResponsibilityRangePreset = '30d' | '90d' | '6m' | 'ytd' | '12m' | 'custom';
/** The presets that are relative to today — everything but a hand-picked range. */
export type FixedResponsibilityPreset = Exclude<ResponsibilityRangePreset, 'custom'>;

export interface IDateOnlyRange {
    /** Inclusive, `YYYY-MM-DD`. */
    start: string;
    /** Inclusive, `YYYY-MM-DD`. */
    end: string;
}

export const DEFAULT_RESPONSIBILITY_PRESET: FixedResponsibilityPreset = '12m';

export const RESPONSIBILITY_RANGE_PRESETS: ReadonlyArray<{ key: FixedResponsibilityPreset; label: string }> = [
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: '6m', label: 'Last 6 months' },
    { key: 'ytd', label: 'Year to date' },
    { key: '12m', label: 'Last 12 months' },
];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n: number): string => String(n).padStart(2, '0');
const toUtc = (date: string): Date => new Date(`${date}T00:00:00Z`);
const fromUtc = (d: Date): string => d.toISOString().slice(0, 10);

export function isDateOnly(value?: string | null): value is string {
    return !!value && DATE_ONLY.test(value) && fromUtc(toUtc(value)) === value;
}

/** The client's LOCAL calendar date. "Today" is the user's today, not UTC's. */
export function localToday(now: Date = new Date()): string {
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * `YYYY-MM-DD` as LOCAL midnight — what a calendar widget (core `DatePicker`, react-day-picker)
 * thinks a day is. The inverse of `localToday(date)`. Not `new Date('YYYY-MM-DD')`, which is UTC
 * midnight and lands on the previous day anywhere west of UTC.
 */
export function toLocalDate(date: string): Date {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d);
}

export function addDays(date: string, days: number): string {
    const d = toUtc(date);
    d.setUTCDate(d.getUTCDate() + days);
    return fromUtc(d);
}

/**
 * Calendar months, clamped to the last day of the target month: Mar 31 − 1 month is Feb 28/29, not
 * Mar 3. That is how .NET's `AddMonths` behaves, and the clamped date is never later than the
 * overflowed one, so the one-year cap can only be stricter than the server's, never looser.
 */
export function addMonths(date: string, months: number): string {
    const d = toUtc(date);
    const day = d.getUTCDate();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + months);
    const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    d.setUTCDate(Math.min(day, lastDay));
    return fromUtc(d);
}

/** Every preset ends yesterday — today is still accumulating claims and is never selectable. */
export function presetRange(preset: FixedResponsibilityPreset, today: string): IDateOnlyRange {
    const end = addDays(today, -1);
    switch (preset) {
        case '30d':
            return { start: addDays(end, -29), end };
        case '90d':
            return { start: addDays(end, -89), end };
        case '6m':
            return { start: addDays(addMonths(end, -6), 1), end };
        case 'ytd':
            return { start: `${end.slice(0, 4)}-01-01`, end };
        case '12m':
            return { start: withinOneYear(addDays(addMonths(end, -12), 1), end), end };
    }
}

/**
 * "Yesterday − 12 months + 1 day" lands on Feb 29 when yesterday is Feb 28 of the following year,
 * and a year after Feb 29 is Feb 28 — so that range is one day over the cap and the server 400s it.
 * Start a day later in that one case.
 */
function withinOneYear(start: string, end: string): string {
    return addDays(end, 1) > addMonths(start, 12) ? addDays(start, 1) : start;
}

/** First failing rule's message, or `undefined` when the range can be sent. */
export function validateResponsibilityRange(range: Partial<IDateOnlyRange>, today: string): string | undefined {
    const { start, end } = range;
    if (!isDateOnly(start) || !isDateOnly(end)) {
        return 'Start and end dates are required';
    }
    const endExclusive = addDays(end, 1);
    if (endExclusive <= start) {
        return 'End date must be on or after start date';
    }
    if (end >= today) {
        return 'End date must be before today';
    }
    if (endExclusive > addMonths(start, 12)) {
        return 'Range cannot exceed one calendar year';
    }
    return undefined;
}

/** Request parameters: the inclusive UI end becomes the API's exclusive end. */
export function toResponsibilityReportParams(range: IDateOnlyRange): { start: string; end: string } {
    return { start: range.start, end: addDays(range.end, 1) };
}

/** `min`/`max` for the two date inputs, so an invalid range is hard to pick in the first place. */
export function responsibilityRangeInputLimits(start: string, today: string): { startMax: string; endMin: string; endMax: string } {
    const yesterday = addDays(today, -1);
    const yearCap = addDays(addMonths(start, 12), -1);
    return { startMax: yesterday, endMin: start, endMax: yearCap < yesterday ? yearCap : yesterday };
}

const labelFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/** `Oct 1, 2025 – Sep 30, 2026`, inclusive end, formatted in UTC so a date-only string cannot shift. */
export function formatResponsibilityRange(range: IDateOnlyRange): string {
    return `${labelFormat.format(toUtc(range.start))} – ${labelFormat.format(toUtc(range.end))}`;
}
